import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { warning } from '../../../../../components/Alert/Alert';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setWeirGeometry } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { IColumn, IWeirGeometryProps, IWeirGeometryVerticalDropForm, IWeirGeometryVerticalDropTable, TWeirGeometryVerticalDropTable } from './IWeirGeometry';
import './WeirGeometry.scss';
import { UnitOfMeasure } from '../../../../enum/project';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';

const heightOptions = [
    { value: 0.3, label: '0.3' },
    { value: 0.5, label: '0.5' },
    { value: 1, label: '1' },
];

const heightOptionsImp = [
    { value: 1, label: '1' },
    { value: 1.5, label: '1.5' },
    { value: 3, label: '3' },
];

const WeirGeometryVerticalDrop = (props: IWeirGeometryProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);

    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const columns = [
        { displayName: t('input_layerNumber', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] }), propertyName: 'layerNumber' },
        { displayName: t('input_layerWidth', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] }), propertyName: 'layerWidth' },
        { displayName: t('input_layerOffset', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] }), propertyName: 'layerOffset' },
        { displayName: t('input_layerHeight', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] }), propertyName: 'layerHeight' },
    ];
    const defaultValue: IWeirGeometryVerticalDropForm = {
        layer: (project.data!.weirGeometry as IWeirGeometryVerticalDropForm).layer,
        foundation: (project.data!.weirGeometry as IWeirGeometryVerticalDropForm).foundation,
    };

    const [foundation, setFoundation] = useState<number>(defaultValue.foundation);
    const [table, setTableElement] = useState<IWeirGeometryVerticalDropTable[]>(defaultValue.layer);
    const HALF_METER_DISPLAY = isImperial ? convertTo(0.5, EConversionSIToImperial.m_to_ft) : 0.5;
    const DEFAULT_DISPLAY = isImperial ? convertTo(1, EConversionSIToImperial.m_to_ft) : 1;
    let oneToImp = isImperial ? roundToTwo(convertTo(1, EConversionSIToImperial.m_to_ft)) : 1;
    let fiveToImp = isImperial ? roundToTwo(convertTo(15, EConversionSIToImperial.m_to_ft)) : 15;

    const clampFoundation = (value: number, data: IWeirGeometryVerticalDropTable[]) => {
        const sigma = data.filter(layer => layer.layerNumber >= 2).reduce((sum, layer) => sum + layer.layerHeight, 0);
        const minBound = Math.min(HALF_METER_DISPLAY, sigma);
        const maxBound = sigma;
        let clampedValue = isNaN(value) ? DEFAULT_DISPLAY : value;

        if (clampedValue < minBound) {
            clampedValue = minBound;
            warning(t('warning_not_be_lower', { value: roundToTwo(minBound) }));
        } else if (clampedValue > maxBound) {
            clampedValue = maxBound;
            warning(t('warning_not_be_higher', { value: roundToTwo(maxBound) }));
        }

        return clampedValue;
    };

    const handleFoundationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFoundation(clampFoundation(parseFloat(e.target.value), table));
    };

    const handleTableChange = (e: React.ChangeEvent<HTMLInputElement>, rowData: any) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        let prevGabion = table.find(y => y.layerNumber == rowData.layerNumber - 1);
        let lastGabion = table[table.length - 1];

        let maxWidth = lastGabion.layerWidth - rowData.layerOffset;
        let minWidth = prevGabion!.layerWidth! + prevGabion!.layerOffset!;

        let maxOffset = lastGabion.layerWidth - rowData.layerWidth;

        if (name == 'layerWidth') {
            if (rowData.layerNumber !== lastGabion.layerNumber) {
                if (value < minWidth) {
                    value = minWidth;
                    warning(t('warning_not_be_lower', { value: minWidth }));
                }
                if (value > maxWidth) {
                    value = maxWidth;
                    warning(t('warning_not_be_higher', { value: maxWidth }));
                }
            } else {
                if (value < prevGabion!.layerWidth + prevGabion!.layerOffset) {
                    value = prevGabion!.layerWidth + prevGabion!.layerOffset;
                    warning(t('warning_not_be_lower', { value: value }));
                }
            }
        } else if (name == 'layerOffset') {
            if (prevGabion!.layerOffset !== 0 || rowData.layerNumber == 2) {
                if (value > maxOffset) {
                    value = maxOffset;
                    warning(t('warning_not_be_higher', { value: maxOffset }));
                }
                if (value > fiveToImp) {
                    value = fiveToImp;
                    warning(t('warning_not_be_higher', { value: value }));
                }
            } else {
                value = 0;
            }
        }

        let newData = table.map(item => {
            if (item.layerNumber === rowData.layerNumber) return { ...item, [name]: value };
            return { ...item };
        });
        newData[0].layerOffset = !isNaN(newData[1].layerOffset) ? newData[1].layerOffset : 0;
        if (name == 'layerHeight') {
            const sigma = newData.filter(layer => layer.layerNumber >= 2).reduce((sum, layer) => sum + layer.layerHeight, 0);
            if (foundation > sigma) {
                setFoundation(clampFoundation(foundation, newData));
            }
        }
        setTableElement(newData);
    };

    const deleteRow = () => {
        let newData = [...table];
        newData.pop();
        const sigma = newData.filter(layer => layer.layerNumber >= 2).reduce((sum, layer) => sum + layer.layerHeight, 0);
        if (foundation > sigma) {
            setFoundation(clampFoundation(foundation, newData));
        }
        setTableElement(newData);
    };

    const addRow = () => {
        setTableElement([
            ...table,
            {
                layerNumber: table.length + 1,
                layerWidth: table[table.length - 1].layerWidth + oneToImp,
                layerOffset: 0,
                layerHeight: oneToImp,
            },
        ]);
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            let _form: IWeirGeometryVerticalDropForm = {
                layer: table,
                foundation: foundation,
            };
            dispatch(cleanResults());
            dispatch(setWeirGeometry(_form));
        }, 1000);
    }, [table, foundation]);

    useEffect(() => {
        if (project.data) {
            let tableWithNewWings = table.map(layer => {
                if (layer.layerNumber == 1) {
                    let firstGabion = (project.data!.weirGeometry! as IWeirGeometryVerticalDropForm).layer[1];
                    let layerHeight = project.data!.wingsGeometry!.height!;
                    let layerWidth = 1;
                    if (layerHeight == 2 || layerHeight == 2.5) {
                        layerWidth = 2;
                    } else if (layerHeight == 3) {
                        if (firstGabion.layerWidth < 3) {
                            layerWidth = 2;
                        } else {
                            layerWidth = 3;
                        }
                    }
                    let newWings: IWeirGeometryVerticalDropTable = {
                        ...layer,
                        layerHeight,
                        layerWidth,
                    };
                    return newWings;
                } else {
                    return layer;
                }
            });
            if (tableWithNewWings[0].layerWidth !== table[0].layerWidth || tableWithNewWings[0].layerHeight !== table[0].layerHeight || tableWithNewWings[0].layerOffset !== table[0].layerOffset) {
                setTableElement(tableWithNewWings);
            }
        }
    }, [project.data?.wingsGeometry]);

    return (
        <>
            <table>
                <thead>
                    <tr>
                        {columns.map((item: IColumn) => (
                            <th key={item.displayName}>{item.displayName}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {table.map((rowData, index) => {
                        return (
                            <tr key={index}>
                                {columns.map((col: IColumn) => {
                                    if (col.propertyName) {
                                        if (index === 0) {
                                            if (col.propertyName === 'layerNumber') return <td key={col.displayName}>Wings</td>;
                                            return <td key={col.displayName}>{rowData[col.propertyName as TWeirGeometryVerticalDropTable]}</td>;
                                        }
                                        if (col.propertyName === 'layerNumber') return <td key={col.displayName}>{rowData[col.propertyName as TWeirGeometryVerticalDropTable]}</td>;
                                        if (col.propertyName === 'layerHeight')
                                            return (
                                                <td key={col.displayName}>
                                                    <SelectCreate
                                                        defaultValue={rowData[col.propertyName as TWeirGeometryVerticalDropTable]}
                                                        name={col.propertyName}
                                                        options={isImperial ? heightOptionsImp : heightOptions}
                                                        parentOnChange={(newValue: any) => {
                                                            let e = { target: { name: col.propertyName, value: newValue } } as any;
                                                            handleTableChange(e, rowData);
                                                        }}
                                                    />
                                                </td>
                                            );
                                        else
                                            return (
                                                <td key={col.displayName}>
                                                    <input
                                                        type='number'
                                                        name={col.propertyName}
                                                        value={rowData[col.propertyName as TWeirGeometryVerticalDropTable]}
                                                        onChange={e => handleTableChange(e, rowData)}
                                                    />
                                                </td>
                                            );
                                    }
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className='d-flex justify-content-end pt-3 gap-3'>
                {table.length > 2 && (
                    <button type='button' className='btn-color-icon-delete' onClick={deleteRow}>
                        <i className='fa-solid fa-trash' />
                    </button>
                )}
                <button type='button' className='btn-color-inverted' onClick={addRow}>
                    <i className='fa-solid fa-plus' />
                </button>
            </div>
            <Input
                type='number'
                title={t('input_foundation', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={foundation}
                name='foundation'
                className='d-flex justify-content-between align-items-center mt-3'
                onChange={handleFoundationChange}
            />
        </>
    );
};

export default WeirGeometryVerticalDrop;
